import { apiClient } from "@lib/api/apiClient";
import type { ApiResponse } from "@lib/api/api-types";
import { formatPersonName } from "@web/utils/formatters";

export interface Profile {
  id: number;
  name: string;
  first_name?: string;
  last_name?: string | null;
  email: string;
  username?: string;
  phone?: string;
  department?: string;
  designation?: string;
  profile_image_url?: string;
  auth_provider?: "LOCAL" | "LDAP";
  role?: string;
  role_name?: string;
  status?: string;
  status_name?: string;
  created_at?: string;
  propertiesListed?: number;
  ticketsRaised?: number;
  savedProperties?: number;
  unreadNotifications?: number;
}

export interface ProfileSettings {
  pushNotifications: boolean;
  emailNotifications: boolean;
  newPropertyAlerts: boolean;
  darkMode: boolean;
  biometricLogin: boolean;
  autoSync: boolean;
}

export type ProfileUpdate = Pick<
  Profile,
  "name" | "last_name" | "phone" | "email"
>;

interface UploadedProfileImage {
  id: number;
  original_name: string;
  stored_name: string;
  file_url: string;
}

export const profileApi = {
  async details() {
    const response = await apiClient.get<ApiResponse<Profile>>("/profile");
    return response.data.data;
  },
  async summary() {
    const response =
      await apiClient.get<ApiResponse<Profile>>("/profile/summary");
    return response.data.data;
  },
  async settings() {
    const response =
      await apiClient.get<ApiResponse<ProfileSettings>>("/profile/settings");
    return response.data.data;
  },
  async update(input: ProfileUpdate) {
    const response = await apiClient.put<ApiResponse<Profile>>("/profile", {
      ...input,
      name: formatPersonName(input.name),
    });
    return response.data.data;
  },
  async uploadImage(file: File) {
    const form = new FormData();
    form.append("file", file);
    const response = await apiClient.post<ApiResponse<UploadedProfileImage>>(
      "/uploads/profile-image",
      form,
    );
    return response.data.data;
  },
  async updateSettings(input: Partial<ProfileSettings>) {
    const response = await apiClient.patch<ApiResponse<ProfileSettings>>(
      "/profile/settings",
      input,
    );
    return response.data.data;
  },
  async changePassword(input: {
    currentPassword: string;
    newPassword: string;
  }) {
    await apiClient.patch("/profile/change-password", input);
  },
};
